import { motion, useAnimationControls } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { springGentle, springQuick, tapScale } from '../tokens';

const REVEAL_PX = 100;
const REVEAL_THRESHOLD = -50;
const COMMIT_THRESHOLD = -180;
const COMMIT_VELOCITY = -800;
/** Any horizontal pointer movement past this is treated as a real drag,
 * which suppresses the tap event. Sub-pixel jitter on a clean tap stays
 * under this and onTap fires normally. */
const TAP_DRAG_TOLERANCE_PX = 6;

type Props = {
  children: ReactNode;
  onDelete: () => void;
  /** Tap on the row body when not revealed. */
  onTap?: () => void;
};

/**
 * iOS-style swipe-to-delete row. Drag the foreground left to reveal a
 * red Delete button; swipe past COMMIT_THRESHOLD (or with velocity) to
 * delete inline. Tapping the row body when revealed snaps it closed
 * before any onTap fires (so a delete-revealed row consumes the next
 * tap).
 *
 * Gesture-vs-tap: Framer's onTap fires after onDragEnd even when a real
 * drag happened, so we mark `didDragRef` whenever onDragEnd sees real
 * horizontal movement and have handleTap bail early on the next tap.
 * Without this, a swipe that revealed the delete button would also fire
 * onTap and open the parent's edit sheet.
 */
export function SwipeRow({ children, onDelete, onTap }: Props) {
  const [revealed, setRevealed] = useState(false);
  const controls = useAnimationControls();
  const didDragRef = useRef(false);

  const close = () => {
    setRevealed(false);
    void controls.start({ x: 0, transition: springGentle });
  };

  const reveal = () => {
    setRevealed(true);
    void controls.start({ x: -REVEAL_PX, transition: springGentle });
  };

  const commitDelete = () => {
    // Slide the row off-screen, then unmount via onDelete.
    void controls
      .start({
        x: -window.innerWidth,
        transition: { duration: 0.22 },
      })
      .then(() => {
        onDelete();
      });
  };

  const handleTap = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (revealed) {
      close();
      return;
    }
    onTap?.();
  };

  return (
    <div className="relative bg-negative">
      {/* Revealed action */}
      <motion.button
        type="button"
        onClick={commitDelete}
        whileTap={tapScale}
        transition={springQuick}
        aria-label="Delete"
        className={cn(
          'absolute inset-y-0 right-0 inline-flex items-center justify-center',
          'text-white font-medium gap-2 px-5 bg-negative',
        )}
        style={{ width: REVEAL_PX }}
      >
        <Trash2 size={16} strokeWidth={1.5} />
        <span className="text-[14px]">Delete</span>
      </motion.button>

      {/* Foreground row */}
      <motion.div
        drag="x"
        animate={controls}
        initial={{ x: 0 }}
        style={{ touchAction: 'pan-y' }}
        dragConstraints={{ left: -REVEAL_PX, right: 0 }}
        dragElastic={0.05}
        dragMomentum={false}
        whileTap={tapScale}
        onDragStart={() => {
          didDragRef.current = false;
        }}
        onDragEnd={(_, info) => {
          if (Math.abs(info.offset.x) > TAP_DRAG_TOLERANCE_PX) {
            didDragRef.current = true;
          }
          if (
            info.offset.x < COMMIT_THRESHOLD ||
            info.velocity.x < COMMIT_VELOCITY
          ) {
            commitDelete();
            return;
          }
          if (info.offset.x < REVEAL_THRESHOLD) {
            reveal();
          } else {
            close();
          }
        }}
        onTap={handleTap}
        className="relative bg-surface"
      >
        {children}
      </motion.div>
    </div>
  );
}
